/* eslint-disable menti-react/filename-convention--jsx */
import React, { useCallback } from 'react';
import {
  type SubmitOpenVoteParams,
  useQuizVoterGameState,
  type VoterConfig,
} from '@mentimeter/quiz';
import { LoaderIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { QuestionWithSlide } from '@mentimeter/voting-schema/api-types-overrides';
import { ClientErrorCodes, GameStateEnums } from '@mentimeter/quiz-types';
import { Lobby } from '../../ui/Components/Quiz/states/Lobby';
import { GetReady } from '../../ui/Components/Quiz/states/GetReady';
import { Countdown } from '../../ui/Components/Quiz/states/Countdown';
import { WaitForResults } from '../../ui/Components/Quiz/states/WaitForResults';
import { Results } from '../../ui/Components/Quiz/states/Results';
import { Error } from '../../ui/Components/Quiz/states/Error';
import { getFirstInteractiveContentChoiceId } from '../../utils/getInteractiveContentChoiceId';
import { useVisualizationColors } from '../useVisualizationColors';
import { useAppSelector } from '../../redux-hooks';
import { getQuizQuestionPositionById, voteKeySelector } from '../../selectors';
import { identifierClient } from '../../utils/identifier';
import type { ModuleComponentProps } from '../types';
import { SubmitForm } from './SubmitForm';

function createSubmitVoteBody(
  question: QuestionWithSlide,
  answer: string,
): SubmitOpenVoteParams {
  if (question.isMigrated) {
    const firstICChoiceId = getFirstInteractiveContentChoiceId(question);

    return {
      isMigrated: true,
      interactiveContentId:
        question.interactiveContents[0]!.interactiveContentId,
      type: 'quiz-open',
      choices: [{ interactiveContentChoiceId: firstICChoiceId, value: answer }],
    };
  }
  return [answer];
}

const App = ({
  config,
  question,
}: { config: VoterConfig } & ModuleComponentProps) => {
  const { fillColors } = useVisualizationColors();
  const { questionIndex, totalNumberOfQuestions } = useAppSelector(
    getQuizQuestionPositionById,
  )(question.id);
  const {
    updatePlayer,
    submitOpenAnswer,
    player,
    gameState,
    timeOffset = 0,
    getPlayerColor,
    connected,
  } = useQuizVoterGameState(config, 'quiz-open');

  const handleSubmit = useCallback(
    (answer: string) => {
      submitOpenAnswer(createSubmitVoteBody(question, answer));
    },
    [question, submitOpenAnswer],
  );

  const playerColor = getPlayerColor(fillColors);

  React.useLayoutEffect(() => {
    // A11y: Focus on heading when step is changed
    document.querySelector('h1')?.focus();
  }, [gameState?.gameState]);

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <Lobby
          connected={Boolean(connected)}
          total={totalNumberOfQuestions}
          current={questionIndex + 1}
          player={player}
          playerColor={playerColor}
          updatePlayer={updatePlayer}
          timeBasedScoring={question.time_based_scoring}
        />
      );
    }
    case GameStateEnums.GET_READY: {
      return <GetReady timeBasedScoring={question.time_based_scoring} />;
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <Countdown
          timeBasedScoring={question.time_based_scoring}
          question={question.question}
          questionStyled={question.question_styled}
          imageUrl={question.image?.presets.medium.url ?? undefined}
          timeOffset={timeOffset}
          startAt={gameState.gameStateData.startAt}
          endAt={gameState.gameStateData.endAt}
        >
          <SubmitForm onSubmitVote={handleSubmit} />
        </Countdown>
      );
    }
    case GameStateEnums.QUESTION: {
      return (
        <WaitForResults
          player={player}
          playerColor={playerColor}
          elapsedTime={gameState.gameStateData.elapsedTime}
        />
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <Results
          player={player}
          playerColor={playerColor}
          result={
            gameState.gameStateData.score?.question?.markedCorrect ?? null
          }
        />
      );
    }
    case GameStateEnums.ERROR: {
      return <Error code={gameState.errorCode} />;
    }
    default: {
      return (
        <Box
          alignItems="center"
          justifyContent="center"
          data-testid="quiz-loading"
        >
          <Error preLoading code={ClientErrorCodes.NO_CONNECTION} />
        </Box>
      );
    }
  }
};

export function Content() {
  return null;
}

export function Interactive({ question }: ModuleComponentProps) {
  const voteKey = useAppSelector(voteKeySelector);
  const { public_key } = question;
  const identifier = identifierClient.getLocalIdentifier().identifier ?? '';

  const config = React.useMemo(() => {
    return {
      voteKey,
      questionPublicKey: public_key,
      identifier,
    };
  }, [identifier, public_key, voteKey]);

  const content = React.useMemo(() => {
    if (!config.identifier || !config.questionPublicKey || !config.identifier) {
      return <LoaderIcon />;
    }
    return <App config={config} question={question} />;
  }, [config, question]);

  return (
    <Box minHeight="40vh" width="100%" alignItems="center">
      {content}
    </Box>
  );
}
